import React, { useState, useEffect } from "react";
import {
    Paper,
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Pagination,
    Autocomplete,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress
} from "@mui/material";
import {
    Refresh as RefreshIcon,
    OpenInNew as OpenIcon,
    Code as CodeIcon,
    Image as ImageIcon
} from "@mui/icons-material";

const ScrapedProductsList = ({
    products,
    pagination,
    selectedDomain,
    loading,
    onLoadProducts,
    onSelectDomain,
    domains
}) => {
    const [filterBrand, setFilterBrand] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [page, setPage] = useState(1);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showStructuredData, setShowStructuredData] = useState(false);

    const limit = 50;

    useEffect(() => {
        if (selectedDomain) {
            loadProducts();
        }
    }, [selectedDomain, page, sortBy, sortOrder]);

    const loadProducts = () => {
        if (!selectedDomain) return;

        onLoadProducts(selectedDomain, {
            limit,
            skip: (page - 1) * limit,
            sort: sortBy,
            sort_order: sortOrder
        });
    };

    const handleDomainChange = (event, newValue) => {
        if (newValue) {
            onSelectDomain(newValue.domain);
            setPage(1);
            setFilterBrand('');
        }
    };

    const handleRefresh = () => {
        loadProducts();
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
        setPage(1);
    };

    const handleSortOrderChange = (event) => {
        setSortOrder(event.target.value);
        setPage(1);
    };

    const handleViewStructuredData = (product) => {
        setSelectedProduct(product);
        setShowStructuredData(true);
    };

    const handleCloseStructuredData = () => {
        setShowStructuredData(false);
        setSelectedProduct(null);
    };

    const getBrandOptions = () => {
        if (!products || products.length === 0) return [];
        
        const brands = new Set();
        products.forEach(product => {
            if (product.brand) brands.add(product.brand);
        });
        return Array.from(brands).sort();
    };

    const filteredProducts = (products && filterBrand)
        ? products.filter(p => p.brand === filterBrand)
        : (products || []);

    const renderFilters = () => {
        return (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            value={(domains || []).find(d => d.domain === selectedDomain) || null}
                            onChange={handleDomainChange}
                            options={domains || []}
                            getOptionLabel={(option) => option.domain}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Domain"
                                    placeholder="Choose a website"
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Typography variant="body2">{option.domain}</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {option.product_count} products
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Filter by Brand</InputLabel>
                            <Select
                                value={filterBrand}
                                onChange={(e) => setFilterBrand(e.target.value)}
                                label="Filter by Brand"
                            >
                                <MenuItem value="">All Brands</MenuItem>
                                {getBrandOptions().map(brand => (
                                    <MenuItem key={brand} value={brand}>
                                        {brand}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={sortBy}
                                onChange={handleSortChange}
                                label="Sort By"
                            >
                                <MenuItem value="created_at">Date Scraped</MenuItem>
                                <MenuItem value="name">Name</MenuItem>
                                <MenuItem value="brand">Brand</MenuItem>
                                <MenuItem value="price.value">Price</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Order</InputLabel>
                            <Select
                                value={sortOrder}
                                onChange={handleSortOrderChange}
                                label="Order"
                            >
                                <MenuItem value="desc">Descending</MenuItem>
                                <MenuItem value="asc">Ascending</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={1}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} color="primary" size="large">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>
        );
    };

    const renderStructuredDataDialog = () => {
        if (!selectedProduct) return null;

        return (
            <Dialog
                open={showStructuredData}
                onClose={handleCloseStructuredData}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Structured Data - {selectedProduct.name}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {selectedProduct.structured_data?.json_ld?.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    JSON-LD ({selectedProduct.structured_data.json_ld.length})
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
                                    <pre style={{ margin: 0, fontSize: '12px' }}>
                                        {JSON.stringify(selectedProduct.structured_data.json_ld, null, 2)}
                                    </pre>
                                </Paper>
                            </Box>
                        )}

                        {selectedProduct.structured_data?.microdata?.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Microdata ({selectedProduct.structured_data.microdata.length})
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
                                    <pre style={{ margin: 0, fontSize: '12px' }}>
                                        {JSON.stringify(selectedProduct.structured_data.microdata, null, 2)}
                                    </pre>
                                </Paper>
                            </Box>
                        )}

                        {selectedProduct.structured_data?.rdfa?.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    RDFa ({selectedProduct.structured_data.rdfa.length})
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
                                    <pre style={{ margin: 0, fontSize: '12px' }}>
                                        {JSON.stringify(selectedProduct.structured_data.rdfa, null, 2)}
                                    </pre>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        );
    };

    const renderProductCard = (product) => {
        const mainImage = product.images?.find(img => img.type === 'main') || product.images?.[0];

        return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {mainImage && (
                        <CardMedia
                            component="img"
                            height="200"
                            image={mainImage.url}
                            alt={product.name}
                            sx={{ objectFit: 'cover' }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    )}
                    {!mainImage && (
                        <Box
                            sx={{
                                height: 200,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.200'
                            }}
                        >
                            <ImageIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                        </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="h6" component="div" noWrap>
                            {product.name || 'Unnamed Product'}
                        </Typography>
                        
                        {product.brand && (
                            <Chip label={product.brand} size="small" sx={{ mb: 1 }} />
                        )}

                        {product.price && (
                            <Typography variant="h6" color="primary">
                                {product.price.currency} {product.price.value}
                            </Typography>
                        )}

                        {product.availability && (
                            <Chip
                                label={product.availability}
                                size="small"
                                color={product.availability === 'InStock' ? 'success' : 'default'}
                                sx={{ mt: 1 }}
                            />
                        )}

                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {product.structured_data?.json_ld?.length > 0 && (
                                <Chip label={`JSON-LD: ${product.structured_data.json_ld.length}`} size="small" variant="outlined" />
                            )}
                            {product.structured_data?.microdata?.length > 0 && (
                                <Chip label={`Microdata: ${product.structured_data.microdata.length}`} size="small" variant="outlined" />
                            )}
                            {product.structured_data?.rdfa?.length > 0 && (
                                <Chip label={`RDFa: ${product.structured_data.rdfa.length}`} size="small" variant="outlined" />
                            )}
                        </Box>
                    </CardContent>

                    <CardActions>
                        <Tooltip title="View on Source Website">
                            <IconButton
                                size="small"
                                href={product.product_url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <OpenIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="View Structured Data">
                            <IconButton
                                size="small"
                                onClick={() => handleViewStructuredData(product)}
                            >
                                <CodeIcon />
                            </IconButton>
                        </Tooltip>
                    </CardActions>
                </Card>
            </Grid>
        );
    };

    if (!selectedDomain) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                    No domain selected
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Select a domain from the "Domains" tab to view products
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            {renderFilters()}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && filteredProducts.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                        No products found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        {filterBrand ? 'Try removing the brand filter' : 'No products available for this domain'}
                    </Typography>
                </Paper>
            )}

            {!loading && filteredProducts.length > 0 && (
                <>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                            Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, pagination.total)} of {pagination.total} products
                            {filterBrand && ` (filtered by brand: ${filterBrand})`}
                        </Typography>
                    </Box>

                    <Grid container spacing={2}>
                        {filteredProducts.map(product => renderProductCard(product))}
                    </Grid>

                    {pagination.total_pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={pagination.total_pages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </>
            )}

            {renderStructuredDataDialog()}
        </Box>
    );
};

export default ScrapedProductsList;
